import { QSelect, QItemSection, QItemLabel, QItem } from "quasar";
import { defineComponent, mergeModels, useModel, ref, computed, watch, createBlock, openBlock, onMounted, withCtx, createVNode, normalizeProps, guardReactiveProps, normalizeClass, createTextVNode, toDisplayString } from "vue";
import { registerEntityEndpoint, VuetifyValidationRules, SEARCH_DEBOUNCE } from "@editor/ui-utils";
import { useI18n } from "vue-i18n";
const de = {
  partnersSelector: {
    allPartners: "Alle partner",
    selectPartner: "Partner"
  },
  partnerList: {
    active: "Status",
    name: "Nach Partnername suchen",
    partnerName: "Partnername",
    importDialog: {
      buttonLabel: "Partner Hinzufügen",
      inputLabel: "Partner-Referenznummer",
      importButtonLabel: "Hinzufügen",
      showCustomPNumberRevShareCheckbox: '"pNumberRevShare" eintragen“',
      customPNumberRevShareInput: "Geben Sie die pNumberRevShare-ID ein",
      customPNumberRevShareTooltipLabel: "Anmerkung",
      customPNumberRevShareTooltip: "Das sollte nur benutzt werden, wenn die Rev Share Nummer nicht in Salesforce enthalten ist. Nur P0000 und P9999 (für interne Marken) können hier eingetragen werden"
    },
    editPartner: "Edit",
    deletePartner: "Löschen",
    detailPartner: "Öffnen",
    messageDeletePartnerName: "Sind Sie sicher, dass Sie diesen Partner löschen möchten?",
    messageDeletePartnerCTFOnlyName: "Der Partner wurde in CA nicht gefunden. Möchten Sie ihn hier in Vibe löschen?",
    messageDeleted: "Partner gelöscht",
    messageDeletePartner: "Partner löschen",
    errorOnDeleting: "Löschen des Partners fehlgeschlagen.",
    reloadPage: "Seite neu laden",
    partnerImportSuccess: "Partner erfolgreich importiert: {name}"
  },
  partnerEdit: {
    logoLoadError: "Logo konnte nicht geladen werden",
    requiredFields: "Sie können den Partner nicht speichern, da obligatorische Informationen fehlen",
    messageSaved: "Partner wurde erfolgreich gespeichert",
    generalTab: "General",
    contractTab: "Vertrag",
    configTab: "Config",
    generalTabFields: {
      indexContent: "Indexieren automatisch veröffentlichter Inhalte",
      indexContentBought: "Indexieren gekaufter Inhalte",
      name: "Partnername",
      isPartner: "Ist Partner",
      account: "Account",
      billingModel: "Abrechnungsmodell",
      pNumberRevShare: "Revenue Share Partner ID",
      partnerId: "Partner-Referenznummer",
      pNumberVideo: "Video Partner ID",
      pNumberOther: "Sonstige Partner ID",
      vibeId: "VIBE Partner ID",
      urlPartner: "Homepage",
      isActiveInCtf: "Aktiv in Vibe",
      pNumberFixum: "Fixum Partner ID",
      urlImprint: "Impressum",
      unpublishAfterDays: { label: "Inhalte deaktivieren nach x Tagen", inputLabel: "Anzahl der Tage" },
      unpublishBoughtAfterDays: { label: "Gekaufte Inhalte deaktivieren nach x Tagen", inputLabel: "Anzahl der Tage" },
      cooperationHeaderType: {
        label: "Kooperations-Kopfzeilentyp",
        customHeaderInputLabel: "Benutzerdefinierten Kopfzeilentext eingeben",
        default: 'Standard: "Erschien in Kooperation mit"',
        custom: "Individuell",
        customInputLabel: "Ihr individueller Kooperationstext"
      }
    },
    contractTabFields: {
      contractModule: "Vertragsmodul",
      contractActive: "Vertrag ist aktiv",
      contractStart: "Vertragsbeginn",
      contractStatus: "Vertragsstatus",
      contractType: "Vertragstyp",
      pricePerTextInEuro: "Preis pro Text in Euro",
      contractEnd: "Vertragsende",
      contractDeviation: "Vertragsabweichung",
      deviationDistribution: "Abweichungsverteilung",
      contentSyndicationNotAllowedOn: "Content-Syndizierung nicht erlaubt am"
    },
    configTabFields: {
      renderConfig: {
        renderConfigSectionLabel: "Partner-Seiten-Renderkonfigurationen",
        tooltipText: "Konfiguration für Partner-Renderings nach Marke",
        pathLabel: "Render-Pfad",
        generateLinkLabel: "Render-Pfad generieren",
        brandDescription: "Partnerbeschreibung",
        isPathActive: "Ist Render-Pfad aktiv",
        invalidPathMessage: 'Der Pfad muss dem Format "/partner/some_string.html" entsprechen',
        goToPartnerPage: "Zur Partner-Seite gehen"
      }
    }
  },
  partnerSync: {
    partnerDataSync: {
      syncPartnerDataLabel: "Partnerdaten synchronisieren",
      dataSyncSuccess: "Partnerdaten wurden erfolgreich synchronisiert"
    },
    partnerLogoSync: {
      syncPartnerLogoLabel: "Partnerlogo synchronisieren",
      logoSyncSuccess: "Partnerlogo wurde erfolgreich synchronisiert"
    },
    allPartnersSync: {
      syncPartners: "Partner synchronisieren",
      messageSyncPartners: "Möchten Sie die Partner mit Salesforce synchronisieren? Bestehende Partner werden aktualisiert, fehlende Partner hinzugefügt, und die Synchronisierung läuft im Hintergrund, sodass Sie Vibe weiterhin nutzen können.",
      messageSyncPartnersName: "Möchten Sie die Partner mit Salesforce synchronisieren?",
      messageSyncPartnersStart: "Partner werden synchronisiert. Dies kann einige Zeit dauern."
    }
  },
  partnerTypes: {
    dropdownLabel: "Partnertyp",
    all: "All",
    contentpartner: "Content-Partner",
    testpartner: "Testpartner",
    bfbrand: "BF-Brand",
    agenturen: "Agentur",
    spezialpartner: "Spezieller Partner"
  },
  activeInVibe: {
    dropdownLabel: "Aktiv in Vibe",
    all: "Alle",
    true: "Aktiv",
    false: "Nicht aktiv"
  }
};
const en = {
  partnersSelector: {
    allPartners: "All partners",
    selectPartner: "Select partner"
  },
  partnerList: {
    active: "Status",
    name: "Search by partner name",
    partnerName: "Partners Name",
    importDialog: {
      buttonLabel: "Add Partner",
      inputLabel: "Parter Reference Number",
      importButtonLabel: "ADD",
      showCustomPNumberRevShareCheckbox: 'Use custom "pNumberRevShare" ID',
      customPNumberRevShareInput: "Enter pNumberRevShare ID",
      customPNumberRevShareTooltipLabel: "Note",
      customPNumberRevShareTooltip: 'Should be used only when "pNumberRevShare" is not available in Salesforce. The only permitted values are P0000 (for agencies) and P9999 (for internal brands)'
    },
    editPartner: "Edit",
    deletePartner: "Delete",
    detailPartner: "Open",
    messageDeletePartnerName: "Are you sure you want to delete this Partner?",
    messageDeletePartnerCTFOnlyName: "Partner was not found in CA. Do you want to delete it here in Vibe?",
    messageDeleted: "Partner Deleted",
    messageDeletePartner: "Delete Partner",
    errorOnDeleting: "Failed to delete partner.",
    reloadPage: "Reload Page",
    partnerImportSuccess: "Successfully imported partner: {name}"
  },
  partnerEdit: {
    logoLoadError: "Couldn't load logo",
    requiredFields: "You can't save Partner because mandatory information is missing",
    messageSaved: "Partner was successfully saved",
    generalTab: "General",
    contractTab: "Contract",
    configTab: "Config",
    generalTabFields: {
      indexContent: "Index Auto-published content",
      indexContentBought: "Index Bought Content",
      name: "Partner Name",
      isPartner: "Is Partner",
      account: "Account",
      billingModel: "Billing Model",
      pNumberRevShare: "Revenue Share Partner ID",
      vibeId: "VIBE Partner ID",
      partnerId: "Parter Reference Number",
      pNumberVideo: "Video Partner ID",
      pNumberOther: "Other Partner ID",
      urlPartner: "Homepage",
      isActiveInCtf: "Active in Vibe",
      pNumberFixum: "Fixum Partner ID",
      urlImprint: "Imprint",
      unpublishAfterDays: { label: "Deactivate Content After Days", inputLabel: "Number of days" },
      unpublishBoughtAfterDays: { label: "Deactivate Bought Content After Days", inputLabel: "Number of days" },
      cooperationHeaderType: {
        label: "Cooperation Header Type",
        customHeaderInputLabel: "Enter custom header text",
        default: 'Default: "Published in cooperation with"',
        custom: "Custom",
        customInputLabel: "Your custom cooperation text"
      }
    },
    contractTabFields: {
      contractModule: "Contract Module",
      contractActive: "Contract Is Active",
      contractStart: "Contract Start Date",
      contractStatus: "Contract Status",
      contractType: "Contract Type",
      pricePerTextInEuro: "Price Per Text In Euro",
      contractEnd: "Contract End Date",
      contractDeviation: "Contract Deviation",
      deviationDistribution: "Deviation Distribution",
      contentSyndicationNotAllowedOn: "Content Syndication Not Allowed On"
    },
    configTabFields: {
      renderConfig: {
        renderConfigSectionLabel: "Partner Pages Render Configs",
        tooltipText: "Configuration for Partners rendered pages by brand",
        pathLabel: "Render Path",
        generateLinkLabel: "Generate Render Path",
        brandDescription: "Partner Description",
        isPathActive: "Is Render Path Active",
        invalidPathMessage: 'Path must be in the format "/partner/some_string.html"',
        goToPartnerPage: "Go to Partners page"
      }
    }
  },
  partnerSync: {
    partnerDataSync: {
      syncPartnerDataLabel: "Sync Partner Data",
      dataSyncSuccess: "Partner data synchronized successfully"
    },
    partnerLogoSync: {
      syncPartnerLogoLabel: "Sync Partner Logo",
      logoSyncSuccess: "Partner logo synchronized successfully"
    },
    allPartnersSync: {
      syncPartners: "Synchronize Partners",
      messageSyncPartners: "Are you sure you want to synchronize partners with Salesforce? Existing partners will be updated, missing partners will be added, and the synchronization will run in the background so you can continue using Vibe.",
      messageSyncPartnersName: "Are you sure you want to synchronize partners with Salesforce?",
      messageSyncPartnersStart: "Synchronizing partners. This may take some time."
    }
  },
  partnerTypes: {
    dropdownLabel: "Partner type",
    all: "All",
    contentpartner: "Content Partner",
    testpartner: "Test Partner",
    bfbrand: "BF Brand",
    agenturen: "Agency",
    spezialpartner: "Special Partner"
  },
  activeInVibe: {
    dropdownLabel: "Active in Vibe",
    all: "All",
    true: "Active",
    false: "Not Active"
  }
};
const messages = { en, de };
const routes = [
  {
    path: "/partners",
    children: [
      {
        path: "",
        name: "partner_list",
        component: () => import("./partner-list.view-BNRaZ7pB.js"),
        meta: { requiredRight: "manage_partners" }
      },
      {
        path: ":id",
        name: "partner_edit",
        component: () => import("./partner-edit.view-DcnDdNyD.js"),
        props: (route) => ({
          id: route.params.id,
          backRoute: "/partners",
          tab: route.query.tab
        }),
        meta: { requiredRight: ["manage_partners"] }
      }
    ]
  }
];
const PartnerModelName = "Partners";
const PartnerService = registerEntityEndpoint(PartnerModelName);
const { listEntities, getEntity, postEntity, getEntityAPIClient } = PartnerService;
const apiClient = getEntityAPIClient();
const getPartnerList = async (search, params = {}) => {
  return listEntities({ params: { search, ...params } });
};
const getPartner = async (id) => {
  return getEntity(id);
};
const postPartner = async (partner) => {
  return postEntity(partner);
};
const putPartner = async (partner) => {
  return await apiClient.put(partner._id, partner);
};
const deletePartner = async (id, onlyCTF) => {
  return await apiClient.delete(`${id}${onlyCTF ? `?onlyCTF=${onlyCTF}` : ""}`);
};
const syncAllPartners = async () => {
  await apiClient.get("sync/all");
};
const syncPartnerLogo = async (id) => {
  const response = await apiClient.get(`sync/logo/${id}`);
  return response.data;
};
const syncPartnerData = async (id) => {
  const response = await apiClient.get(`sync/data/${id}`);
  return response.data;
};
const importPartnerFromSf = async (partnerId, customPNumberRevShare) => {
  return postEntity({ partnerId, customPNumberRevShare });
};
const getEmptyPartner = () => ({
  _id: "",
  name: "",
  partnerId: "",
  urlPartner: "https://www.someurl.com/",
  urlLogo: "https://www.someurl.com/",
  pNumberRevShare: "P0001",
  isActiveInCtf: true,
  partnerType: "contentpartner"
});
const _sfc_main$1 = /* @__PURE__ */ defineComponent({
  __name: "partner-filter.selector",
  props: /* @__PURE__ */ mergeModels({
    multiple: { type: Boolean, required: false, default: false },
    disable: { type: Boolean, required: false, default: false },
    required: { type: Boolean, required: false, default: false }
  }, {
    "modelValue": { type: [String, Array], ...{ default: "" } },
    "modelModifiers": {}
  }),
  emits: ["update:modelValue"],
  setup(__props, { expose: __expose }) {
    __expose();
    const { t } = useI18n();
    const props = __props;
    const model = useModel(__props, "modelValue");
    const partnerList = ref([]);
    const partnersOptions = computed(
      () => partnerList.value.map(
        (partner) => ({
          label: `${partner.name}`,
          value: partner._id
        })
      )
    );
    const rules = computed(() => props.required ? [VuetifyValidationRules.required] : []);
    function filterSearch(search, update) {
      update(async () => {
        const { list } = await getPartnerList(search, { searchfields: ["name"] });
        partnerList.value = list;
      });
    }
    const loadSelectedPartners = async () => {
      if (!model.value)
        return [];
      const selected = Array.isArray(model.value) ? [...model.value] : [model.value];
      const list = new Set(partnerList.value.map((partner) => partner._id));
      return Promise.all(selected.filter((uuid) => !list.has(uuid)).map(getPartner));
    };
    watch(
      () => model.value,
      async () => {
        const { list } = await getPartnerList();
        const selected = await loadSelectedPartners();
        partnerList.value = [...selected, ...list];
      },
      { immediate: true }
    );
    const __returned__ = { t, props, model, partnerList, partnersOptions, rules, filterSearch, loadSelectedPartners, get SEARCH_DEBOUNCE() {
      return SEARCH_DEBOUNCE;
    }, get QSelect() {
      return QSelect;
    } };
    Object.defineProperty(__returned__, "__isScriptSetup", { enumerable: false, value: true });
    return __returned__;
  }
});
const _export_sfc = (sfc, props) => {
  const target = sfc.__vccOpts || sfc;
  for (const [key, val] of props) {
    target[key] = val;
  }
  return target;
};
function _sfc_render$1(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createBlock($setup["QSelect"], {
    modelValue: $setup.model,
    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => $setup.model = $event),
    "data-qa-partner-filter-dropdown": "",
    dense: "",
    outlined: "",
    label: $setup.t("partnersSelector.selectPartner"),
    "use-chips": $props.multiple,
    multiple: $props.multiple,
    "input-debounce": $setup.SEARCH_DEBOUNCE,
    "emit-value": "",
    "map-options": "",
    "use-input": "",
    clearable: !$props.required,
    "clear-icon": $props.required ? void 0 : "x",
    "dropdown-icon": "expand_more",
    disable: $props.disable,
    options: $setup.partnersOptions,
    rules: $setup.rules,
    onFilter: $setup.filterSearch
  }, null, 8, ["modelValue", "label", "use-chips", "multiple", "input-debounce", "clearable", "clear-icon", "disable", "options", "rules"]);
}
const partnerFilter_selector = /* @__PURE__ */ _export_sfc(_sfc_main$1, [["render", _sfc_render$1], ["__file", "/builds/CT/content-technology-framework/modules/partner/editor-ui/src/selectors/partner-filter.selector.vue"]]);
const _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "partner-list.selector",
  props: /* @__PURE__ */ mergeModels({
    label: { type: String, required: false, default: void 0 },
    multiple: { type: Boolean, required: false },
    rules: { type: Array, required: false, default: void 0 },
    allowBlankOption: { type: Boolean, required: false, default: true },
    blankOptionLabelKey: { type: String, required: false, default: "partnersSelector.allPartners" },
    blankOptionValue: { type: String, required: false, default: "" },
    showActiveInCtfOnly: { type: Boolean, required: false, default: true },
    highlightIfNotActiveInCtf: { type: Boolean, required: false, default: false }
  }, {
    "modelValue": { type: String, ...{ default: "" } },
    "modelModifiers": {}
  }),
  emits: ["update:modelValue"],
  setup(__props, { expose: __expose }) {
    __expose();
    const props = __props;
    const partners = ref();
    const partnersFilter = ref("");
    const { t } = useI18n();
    const model = useModel(__props, "modelValue");
    onMounted(async () => {
      try {
        const { list } = await PartnerService.listEntities({
          params: {
            size: 999,
            ...props.showActiveInCtfOnly ? { isActiveInCtf: true } : {},
            sortBy: "name",
            sortDesc: false
          }
        });
        partners.value = list;
      } catch (e) {
        partners.value = [];
      }
    });
    const filterPartners = (filterVal, update) => {
      update(() => partnersFilter.value = filterVal);
    };
    const getPartnerOptions = computed(() => {
      var _a;
      const partnersFiltered = ((_a = partners.value) == null ? void 0 : _a.filter(
        (partner) => partnersFilter.value === "" || partner.name.toLowerCase().indexOf(partnersFilter.value.toLowerCase()) > -1
      )) || [];
      const mappedPartners = partnersFiltered.map((partner) => ({
        label: partner.name,
        value: partner._id,
        highlight: !partner.isActiveInCtf && props.highlightIfNotActiveInCtf
      })) || [];
      props.allowBlankOption && mappedPartners.unshift({ label: t(props.blankOptionLabelKey), value: props.blankOptionValue, highlight: false });
      return mappedPartners;
    });
    const __returned__ = { props, partners, partnersFilter, t, model, filterPartners, getPartnerOptions, get QItem() {
      return QItem;
    }, get QItemLabel() {
      return QItemLabel;
    }, get QItemSection() {
      return QItemSection;
    }, get QSelect() {
      return QSelect;
    } };
    Object.defineProperty(__returned__, "__isScriptSetup", { enumerable: false, value: true });
    return __returned__;
  }
});
function _sfc_render(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createBlock($setup["QSelect"], {
    modelValue: $setup.model,
    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => $setup.model = $event),
    options: $setup.getPartnerOptions,
    label: $props.label,
    multiple: $props.multiple,
    "use-chips": $props.multiple,
    rules: $props.rules,
    "emit-value": "",
    "map-options": "",
    dense: "",
    flat: "",
    "use-input": "",
    "dropdown-icon": "expand_more",
    outlined: "",
    class: "partner-list-selector",
    onFilter: $setup.filterPartners
  }, {
    option: withCtx((scope) => [
      createVNode(
        $setup["QItem"],
        normalizeProps(guardReactiveProps(scope.itemProps)),
        {
          default: withCtx(() => [
            createVNode(
              $setup["QItemSection"],
              null,
              {
                default: withCtx(() => [
                  createVNode($setup["QItemLabel"], {
                    class: normalizeClass(scope.opt.highlight ? "text-red" : "")
                  }, {
                    default: withCtx(() => [
                      createTextVNode(
                        toDisplayString(scope.opt.label),
                        1
                        /* TEXT */
                      )
                    ]),
                    _: 2
                    /* DYNAMIC */
                  }, 1032, ["class"])
                ]),
                _: 2
                /* DYNAMIC */
              },
              1024
              /* DYNAMIC_SLOTS */
            )
          ]),
          _: 2
          /* DYNAMIC */
        },
        1040
        /* FULL_PROPS, DYNAMIC_SLOTS */
      )
    ]),
    _: 1
    /* STABLE */
  }, 8, ["modelValue", "options", "label", "multiple", "use-chips", "rules"]);
}
const partnerList_selector = /* @__PURE__ */ _export_sfc(_sfc_main, [["render", _sfc_render], ["__file", "/builds/CT/content-technology-framework/modules/partner/editor-ui/src/selectors/partner-list.selector.vue"]]);
const partnerModule = {
  messages,
  routes
};
export {
  PartnerService as P,
  _export_sfc as _,
  syncPartnerLogo as a,
  syncPartnerData as b,
  partnerModule as c,
  deletePartner as d,
  partnerFilter_selector as e,
  partnerList_selector as f,
  getPartner as g,
  PartnerModelName as h,
  importPartnerFromSf as i,
  getPartnerList as j,
  postPartner as k,
  getEmptyPartner as l,
  putPartner as p,
  syncAllPartners as s
};
//# sourceMappingURL=index-C78I6Tud.js.map
